import type { EmptyObject } from 'type-fest'
import { z } from 'zod'
import {
  IntegrationProjectsFilterField,
  Sage100InvoiceTaxType,
  type IntegrationSyncResultCode,
} from '../enums.js'
import { VISTA_CHANGE_ORDER_DESCRIPTION_MAX_CHARS } from '../utils/change-order-request.js'
import {
  MAX_CMIC_INVOICE_NUMBER_LENGTH,
  MAX_FOUNDATION_INVOICE_NUMBER_LENGTH,
  MAX_QUICKBOOKS_ONLINE_INVOICE_NUMBER_LENGTH,
  MAX_SAGE_300_DRAW_NUMBER_LENGTH,
  MAX_SAGE_INTACCT_REFERENCE_NUMBER_LENGTH,
  MAX_SAGE_INVOICE_CODE_LENGTH,
  MAX_SPECTRUM_BATCH_NUMBER_LENGTH,
  MAX_SPECTRUM_INVOICE_CODE_LENGTH,
  MAX_VISTA_BATCH_NUMBER_LENGTH,
  MAX_VISTA_INVOICE_NUMBER_LENGTH,
} from '../utils/integration-sync.js'
import {
  zodDaySchema,
  zodIntegrationEntityIdSchema,
  zodUuidSchema,
} from '../utils/validation-zod.js'

export type IntegrationMappingsTextura = {
  type: 'textura'
  project: {
    texturaProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    texturaContractId?: string
    contractNumber?: string
  }
  legalDocumentRequirements: {
    sitelineLegalRequirementId: string
    texturaLegalDocumentRequirementId: string
    isRecurring: boolean
  }[]
  vendorContracts: {
    sitelineVendorId: string
    texturaVendorContractId: string
  }[]
}
export type IntegrationMappingsGcPay = {
  type: 'gcPay'
  project: {
    gcPayProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    gcPaySovId?: string
    contractNumber?: string
  }
}

export type IntegrationMappingsAcumatica = {
  type: 'acumatica'
  project: {
    agaveProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    agaveContractId?: string
    contractNumber?: string
  }
  customer?: {
    agaveCustomerId: string
    name: string
  }
}

export type IntegrationMappingsAcumaticaPeyote = {
  type: 'acumaticaPeyote'
  project: {
    peyoteProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    peyoteContractId?: string
    contractNumber?: string
  }
  customer?: {
    peyoteCustomerId: string
    name: string
  }
}

export type IntegrationMappingsSage100Contractor = {
  type: 'sage100Contractor'
  project: {
    hh2ProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    hh2ContractId?: string
    contractNumber?: string
  }
  incomeAccount?: {
    hh2AccountId: string
    hh2AccountName?: string
    hh2AccountCode?: string
  }
  defaultInvoiceTaxType?: Sage100InvoiceTaxType
  defaultIntegrationTaxAccountId?: string

  // Default list of line items that represent tax amounts, to handle
  // the case of a post-tax SOV where taxes are explicitly inserted
  // as separate line items.
  defaultSitelineTaxLineItemIds?: string[]
}

export type IntegrationMappingsSage100ContractorAgave = {
  type: 'sage100ContractorAgave'
  project: {
    agaveProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    agaveContractId?: string
    contractNumber?: string
  }
  customer?: {
    agaveCustomerId: string
    name: string
  }
  incomeAccount?: {
    agaveAccountId: string
    agaveAccountName?: string
    agaveAccountCode?: string

    subsidiaryAccountRef?: string
    subsidiaryAccountName?: string
    subsidiaryAccountCode?: string
  }
  defaultInvoiceTaxType?: Sage100InvoiceTaxType
  defaultIntegrationTaxAccountId?: string
  defaultTaxSubsidiaryAccountRef?: string

  // Default list of line items that represent tax amounts, to handle
  // the case of a post-tax SOV where taxes are explicitly inserted
  // as separate line items.
  defaultSitelineTaxLineItemIds?: string[]
}

export type IntegrationMappingsSage100ContractorPeyote = {
  type: 'sage100ContractorPeyote'
  project: {
    peyoteProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    peyoteContractId?: string
    contractNumber?: string
  }
  customer?: {
    peyoteCustomerId: string
    name: string
  }
  incomeAccount?: {
    peyoteAccountId: string
    peyoteAccountName?: string
    peyoteAccountCode?: string

    subsidiaryAccountRef?: string
    subsidiaryAccountName?: string
    subsidiaryAccountCode?: string
  }
  defaultInvoiceTaxType?: Sage100InvoiceTaxType
  defaultIntegrationTaxAccountId?: string
  defaultTaxSubsidiaryAccountRef?: string

  // Default list of line items that represent tax amounts, to handle
  // the case of a post-tax SOV where taxes are explicitly inserted
  // as separate line items.
  defaultSitelineTaxLineItemIds?: string[]
}

export type IntegrationMappingsSage300Cre = {
  type: 'sage300Cre'
  project: {
    hh2ProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    hh2ContractId?: string
    contractNumber?: string
  }
  taxGroup?: {
    hh2TaxGroupId: string
    name: string
    taxPercent: number
  }
  customer?: {
    hh2CustomerId: string
    name: string
  }
  /**
   * Additional hh2 Job IDs that share the same physical project as `project.hh2ProjectId`.
   * Populated at import time so AP invoice reads (and the bulk-import cleanup query) roll up
   * every related job. Sage 300 CRE's "combo jobs" pattern stores G&A costs on one Job and
   * subcontractor commitments on another; both need to feed into the same Siteline contract.
   */
  associatedProjectIds?: string[]
}
export type IntegrationMappingsSage300CreAgave = {
  type: 'sage300CreAgave'
  project: {
    agaveProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    agaveContractId?: string
    contractNumber?: string
  }
  taxGroup?: {
    agaveTaxGroupId: string
    name: string
    taxPercent: number
  }
  customer?: {
    agaveCustomerId: string
    name: string
  }
  /**
   * Additional Sage project IDs (via Agave) that share the same physical project as
   * `project.agaveProjectId`. See the `IntegrationMappingsSage300Cre` variant for details.
   */
  associatedProjectIds?: string[]
}
export type IntegrationMappingsViewpointSpectrum = {
  type: 'spectrum'
  project: {
    agaveProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    agaveContractId?: string
    contractNumber?: string
  }
  customer?: {
    agaveCustomerId: string
    name: string
  }
  taxGroup?: {
    agaveTaxGroupId: string
    name: string
    code: string
    taxPercent: number
  }
}
export type IntegrationMappingsViewpointSpectrumPeyote = {
  type: 'spectrumPeyote'
  project: {
    peyoteProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    peyoteContractId?: string
    contractNumber?: string
  }
  customer?: {
    peyoteCustomerId: string
    name: string
  }
  taxGroup?: {
    peyoteTaxGroupId: string
    name: string
    code: string
    taxPercent: number
  }
}

export type IntegrationMappingsViewpointVista = {
  type: 'vista'
  project: {
    agaveProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    agaveContractId?: string
    contractNumber?: string
  }
  customer?: {
    agaveCustomerId: string
    name: string
  }
  taxGroup?: {
    agaveTaxGroupId: string
    name: string
    code: string
    taxPercent: number
  }
  writeToJbBilling?: boolean
  associatedProjectIds?: string[]
}

export type IntegrationMappingsViewpointVistaPeyote = {
  type: 'vistaPeyote'
  project: {
    peyoteProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    peyoteContractId?: string
    contractNumber?: string
  }
  customer?: {
    peyoteCustomerId: string
    name: string
  }
  taxGroup?: {
    peyoteTaxGroupId: string
    name: string
    code: string
    taxPercent: number
  }
  writeToJbBilling?: boolean
  associatedProjectIds?: string[]
}

export type IntegrationMappingsQuickbooksOnline = {
  type: 'quickbooksOnline'
  project: {
    quickbooksProjectId?: string
    name?: string
    projectNumber?: string
  }
  customer?: {
    quickbooksCustomerId: string
    name: string
  }
  item?: {
    integrationItemId: string
  }
}

export type IntegrationMappingsQuickbooksOnlinePeyote = {
  type: 'quickbooksOnlinePeyote'
  project: {
    quickbooksProjectId?: string
    name?: string
    projectNumber?: string
  }
  customer?: {
    quickbooksCustomerId: string
    name: string
  }
  item?: {
    peyoteItemId: string
  }
}

export type IntegrationMappingsProcore = {
  type: 'procore'
  associatedCompanyId?: string
  project: {
    agaveProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    agaveContractId?: string
    contractNumber?: string
  }
}

export type IntegrationMappingsProcorePeyote = {
  type: 'procorePeyote'
  peyoteAssociatedCompanyId?: string
  project: {
    peyoteProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    peyoteContractId?: string
    contractNumber?: string
  }
}

export type FoundationLineItemMapping = {
  sitelineLineItemId: string
  agaveLedgerAccountId: string
}

type FoundationPeyoteLineItemMapping = {
  sitelineLineItemId: string
  peyoteLedgerAccountId: string
}

export type IntegrationMappingsFoundation = {
  type: 'foundation'
  project: {
    agaveProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    agaveContractId?: string
    contractNumber?: string
  }
  customer?: {
    agaveCustomerId: string
    name: string
  }
  // One of ledgerAccount or lineItemMappings must be defined
  // Mappings if the customer uses just one default ledger account and income type
  ledgerAccount?: {
    agaveAccountId: string
    name?: string
    code?: string
  }

  // Mappings if the customer assigns a different ledger account or income type to each line
  lineItemMappings?: FoundationLineItemMapping[]

  // Separate ledger account for retention, which we call "Retainage" in the UI
  retentionLedgerAccount?: {
    agaveSourceId: string
    name?: string
  }

  // Separate ledger account for A/R
  arLedgerAccount?: {
    agaveSourceId: string
    name?: string
  }

  // Agave does not return uuids for income types, so we store the income type id in the mappings
  // directly
  incomeTypeId?: string | null
}

export type IntegrationMappingsFoundationPeyote = {
  type: 'foundationPeyote'
  project: {
    peyoteProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    peyoteContractId?: string
    contractNumber?: string
  }
  customer?: {
    peyoteCustomerId: string
    name: string
  }
  // One of ledgerAccount or lineItemMappings must be defined
  // Mappings if the customer uses just one default ledger account and income type
  ledgerAccount?: {
    peyoteAccountId: string
    name?: string
    code?: string
  }

  // Mappings if the customer assigns a different ledger account or income type to each line
  lineItemMappings?: FoundationPeyoteLineItemMapping[]

  // Separate ledger account for retention, which we call "Retainage" in the UI
  retentionLedgerAccount?: {
    peyoteSourceId: string
    name?: string
  }

  // Separate ledger account for A/R
  arLedgerAccount?: {
    peyoteSourceId: string
    name?: string
  }

  incomeTypeId?: string | null
}

export type IntegrationMappingsSageIntacct = {
  type: 'sageIntacct'
  project: {
    agaveProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    agaveContractId?: string
    contractNumber?: string
  }
  customer?: {
    agaveCustomerId: string
    name: string
  }
}

export type IntegrationMappingsSageIntacctPeyote = {
  type: 'sageIntacctPeyote'
  project: {
    peyoteProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract: {
    peyoteContractId?: string
    contractNumber?: string
  }
  customer?: {
    peyoteCustomerId: string
    name: string
  }
}

export type IntegrationMappingsCmic = {
  type: 'cmic'
  project: {
    agaveProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract?: {
    // NOTE that this is a CMiC contract code, not an Agave contract id
    // This is because we are pulling the CMiC contract via passthrough, so
    // Agave is not generating a UUID for it
    cmicContractCode?: string
    contractNumber?: string
  }
  customer?: {
    // NOTE that we are also pulling the CMiC customer via passthrough, so Agave
    // is not generating a UUID for it. This code is generated by CMiC itself
    cmicCustomerCode: string
    name: string
  }
}

export type IntegrationMappingsCmicPeyote = {
  type: 'cmicPeyote'
  project: {
    peyoteProjectId?: string
    name?: string
    projectNumber?: string
  }
  contract?: {
    // The CMiC contract code, generated by CMiC itself (not a Peyote id)
    cmicContractCode?: string
    contractNumber?: string
  }
  customer?: {
    // The CMiC customer code, generated by CMiC itself (not a Peyote id)
    cmicCustomerCode: string
    name: string
  }
}

export type IntegrationMappingsTest = {
  type: 'test'
}
export type IntegrationMappingsFile = {
  type: 'file'
}
export type IntegrationMappings =
  | IntegrationMappingsAcumatica
  | IntegrationMappingsAcumaticaPeyote
  | IntegrationMappingsTextura
  | IntegrationMappingsGcPay
  | IntegrationMappingsProcore
  | IntegrationMappingsProcorePeyote
  | IntegrationMappingsFoundation
  | IntegrationMappingsFoundationPeyote
  | IntegrationMappingsCmic
  | IntegrationMappingsCmicPeyote
  | IntegrationMappingsSageIntacct
  | IntegrationMappingsSageIntacctPeyote
  | IntegrationMappingsSage100Contractor
  | IntegrationMappingsSage100ContractorAgave
  | IntegrationMappingsSage100ContractorPeyote
  | IntegrationMappingsSage300Cre
  | IntegrationMappingsSage300CreAgave
  | IntegrationMappingsViewpointSpectrum
  | IntegrationMappingsViewpointSpectrumPeyote
  | IntegrationMappingsViewpointVista
  | IntegrationMappingsViewpointVistaPeyote
  | IntegrationMappingsTest
  | IntegrationMappingsFile
  | IntegrationMappingsQuickbooksOnline
  | IntegrationMappingsQuickbooksOnlinePeyote

export type VendorIntegrationMappings = {
  integrations?: {
    companyIntegrationId: string
    integrationVendorId: string
  }[]

  // Deprecated. These mappings are preserved for legacy reasons but are not maintained and should
  // not be used or relied on.
  hh2?: {
    vendorId?: string
  }
  agave?: {
    vendorId?: string
  }
}

export type LocationIntegrationMappings = {
  integrations?: {
    companyIntegrationId: string
    /**
     * The native Sage Intacct LOCATIONID (e.g., "ES-010"), not an Agave UUID.
     * We fetch locations via passthrough requests, because Agave does not have
     * a locations API for Sage Intacct.
     */
    sageIntacctLocationId: string
  }[]
}

export type GeneralContractorMappingComputerEase = {
  type: 'computerEase'
  customerId: string
}

type GeneralContractorMappingTest = {
  type: 'test'
}

export type GeneralContractorMapping =
  | GeneralContractorMappingComputerEase
  | GeneralContractorMappingTest

export type GeneralContractorMappings = {
  companyId: string
  mappings: GeneralContractorMapping[]
}

export type CompanyIntegrationMappings = {
  /** Integration mappings related to the company's GCs */
  generalContractorMappings?: GeneralContractorMappings[]
}

export const writeSyncPayloadPayAppTexturaSchema = z.object({
  type: z.literal('payAppTextura'),
  payAppId: zodUuidSchema,
})

export type WriteSyncPayloadPayAppTextura = z.infer<typeof writeSyncPayloadPayAppTexturaSchema>

export const writeSyncPayloadPayAppGcPaySchema = z.object({
  type: z.literal('payAppGcPay'),
  payAppId: zodUuidSchema,
})
type WriteSyncPayloadPayAppGcPay = z.infer<typeof writeSyncPayloadPayAppGcPaySchema>

export const writeSyncPayloadPayAppProcoreSchema = z.object({
  type: z.literal('payAppProcore'),
  payAppId: zodUuidSchema,
})
export type WriteSyncPayloadPayAppProcore = z.infer<typeof writeSyncPayloadPayAppProcoreSchema>

// We need to add integrationId to the normal write sync payloads for bulk sync
// because we need to do validation on the integration and don't pass the integration id
// as a separate arg
const integrationIdForBulkSyncSchema = z.object({ integrationId: zodUuidSchema })

export const writeSyncPayloadPayAppSage100Schema = z.object({
  type: z.literal('payAppSage100'),
  payAppId: zodUuidSchema,
  invoiceCode: z.string().min(1).max(MAX_SAGE_INVOICE_CODE_LENGTH),
  invoiceDate: zodDaySchema,
  dueDate: zodDaySchema,
  /**
   * For the hh2-backed integration, this is the subsidiary account ID if using a subsidiary
   * account. If the account has no subsidiary, this is the parent account ID.
   * For the Agave/Peyote-backed integration, this is always the parent acconut ID.
   */
  integrationAccountId: zodIntegrationEntityIdSchema,
  integrationAccountName: z.string().optional(),
  integrationAccountCode: z.string().optional(),
  /**
   * Used only for Agave/Peyote-backed integration if using a subsidiary account. This is the recnum of the
   * subsidiary account, not a UUID.
   */
  subsidiaryAccountRef: z.string().min(1).optional(),
  subsidiaryAccountName: z.string().optional(),
  subsidiaryAccountCode: z.string().optional(),

  // How to handle taxes on the invoice
  invoiceTaxType: z.enum(Sage100InvoiceTaxType).optional(),

  // Only allowed if tax type is INVOICE_DISTRIBUTION or SITELINE_SOV_POST_TAX.
  // If not provided, taxes will go to the default account.
  integrationTaxAccountId: zodIntegrationEntityIdSchema.optional(),

  /** See `subsidiaryAccountRef` above; used only for Agave-backed integration */
  taxSubsidiaryAccountRef: z.string().min(1).optional(),

  // Only allowed if tax type is SITELINE_SOV_POST_TAX
  // If provided, tax will be calculated from line items in the SOV (which is post-tax),
  // as opposed to using Siteline tax groups.
  // This is to handle the case of a post-tax SOV where taxes are explicitly inserted
  // as separate line items.
  sitelineTaxLineItemIds: z.array(zodUuidSchema).optional(),
})
export type WriteSyncPayloadPayAppSage100 = z.infer<typeof writeSyncPayloadPayAppSage100Schema>

export const writeSyncPayloadPayAppSage100ForBulkSyncSchema = z.intersection(
  writeSyncPayloadPayAppSage100Schema,
  integrationIdForBulkSyncSchema
)
export type WriteSyncPayloadPayAppSage100ForBulkSync = z.infer<
  typeof writeSyncPayloadPayAppSage100ForBulkSyncSchema
>

export const writeSyncPayloadPayAppFoundationSchema = z
  .object({
    type: z.literal('payAppFoundation'),
    payAppId: zodUuidSchema,
    invoiceDate: zodDaySchema,
    dueDate: zodDaySchema,

    // The customer can enter an invoice code manually
    // or leave the field blank to allow Foundation to generate the code
    invoiceNumber: z.string().min(1).max(MAX_FOUNDATION_INVOICE_NUMBER_LENGTH).optional(),

    // We use Foundation source account code for retention
    // this refers to a ledger account in which retention is held
    // see https://sitelinetech.slack.com/archives/C03RTRXJYCV/p1715704734297519?thread_ts=1712187177.353769&cid=C03RTRXJYCV
    integrationLedgerAccountCodeForRetainage: z.string().optional(),
    integrationLedgerAccountNameForRetainage: z.string().optional(),

    // Ledger account to hold AR
    integrationLedgerAccountCodeForAr: z.string().optional().nullable(),
    integrationLedgerAccountNameForAr: z.string().optional(),

    // Mappings if the customer assigns a different ledger account or income type to each line
    lineItemMappings: z
      .array(
        z.object({
          sitelineLineItemId: zodUuidSchema,
          agaveLedgerAccountId: zodUuidSchema,
        })
      )
      .optional(),

    // Mappings if the customer uses just one default ledger account and income type
    integrationLedgerAccountId: zodIntegrationEntityIdSchema.optional(),
    integrationLedgerAccountName: z.string().optional(),
    integrationLedgerAccountCode: z.string().optional(),
    // Income type is required if the customer has income types enabled; otherwise, we pass null
    incomeTypeId: z.string().min(1).nullable(),
  })
  .refine(
    (data) =>
      (data.lineItemMappings && !data.integrationLedgerAccountId) ||
      (!data.lineItemMappings && data.integrationLedgerAccountId),
    {
      message: 'Only one of lineItemMappings or integrationLedgerAccountId must be provided',
    }
  )
export type WriteSyncPayloadPayAppFoundation = z.infer<
  typeof writeSyncPayloadPayAppFoundationSchema
>

export const writeSyncPayloadPayAppFoundationForBulkSyncSchema = z.intersection(
  writeSyncPayloadPayAppFoundationSchema,
  integrationIdForBulkSyncSchema
)
export type WriteSyncPayloadPayAppFoundationForBulkSync = z.infer<
  typeof writeSyncPayloadPayAppFoundationForBulkSyncSchema
>

export const writeSyncPayloadPayAppCmicSchema = z.object({
  type: z.literal('payAppCmic'),
  payAppId: zodUuidSchema,
  invoiceDate: zodDaySchema,
  invoiceCode: z.string().min(1),
  dueDate: zodDaySchema,
})
export type WriteSyncPayloadPayAppCmic = z.infer<typeof writeSyncPayloadPayAppCmicSchema>

export const writeSyncPayloadPayAppCmicForBulkSyncSchema = z.intersection(
  writeSyncPayloadPayAppCmicSchema,
  integrationIdForBulkSyncSchema
)
export type WriteSyncPayloadPayAppCmicForBulkSync = z.infer<
  typeof writeSyncPayloadPayAppCmicForBulkSyncSchema
>

// Payload used when marking a pay app as synced manually.
// This payload is never processed by the integrations service, it's essentially just a fake row
// inserted in the database to keep track of manual syncs.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const writeSyncPayloadPayAppManualSchema = z.object({
  type: z.literal('payAppManual'),
  payAppId: zodUuidSchema,
})
export type WriteSyncPayloadPayAppManual = z.infer<typeof writeSyncPayloadPayAppManualSchema>

export const writeSyncPayloadPayAppQuickbooksSchema = z.object({
  type: z.literal('payAppQuickbooks'),
  payAppId: zodUuidSchema,

  // The following fields are not provided when bulk exporting
  invoiceNumber: z.string().optional(),
  projectName: z.string().optional(),
  customerName: z.string().optional(),
  includeRetention: z.boolean().optional(),
  combineAsSingleLine: z.boolean().optional(),
  singleLineDescription: z.string().optional(),
})
export type WriteSyncPayloadPayAppQuickbooks = z.infer<
  typeof writeSyncPayloadPayAppQuickbooksSchema
>

export const writeSyncPayloadPayAppFoundationFileGenieSchema = z.object({
  type: z.literal('payAppFoundationFileGenie'),
  payAppId: zodUuidSchema,
})
export type WriteSyncPayloadPayAppFoundationFileGenie = z.infer<
  typeof writeSyncPayloadPayAppFoundationFileGenieSchema
>

export const writeSyncPayloadPayAppFoundationFileFsiSchema = z.object({
  type: z.literal('payAppFoundationFileFsi'),
  payAppId: zodUuidSchema,
  customerNumber: z.string(),
  projectNumber: z.string(),
})
export type WriteSyncPayloadPayAppFoundationFileFsi = z.infer<
  typeof writeSyncPayloadPayAppFoundationFileFsiSchema
>

export const writeSyncPayloadPayAppComputerEaseSchema = z.object({
  type: z.literal('payAppComputerEase'),
  payAppId: zodUuidSchema,
  customerId: z.string().optional(),
  jobId: z.string().optional(),
  arAccount: z.string().optional(),
  salesAccount: z.string().optional(),
})
export type WriteSyncPayloadPayAppComputerEase = z.infer<
  typeof writeSyncPayloadPayAppComputerEaseSchema
>

export const writeSyncPayloadPayAppNetSuiteSchema = z.object({
  type: z.literal('payAppNetSuite'),
  payAppId: zodUuidSchema,
  subsidiary: z.string().min(1),
  arAccount: z.string().min(1),
  progressItem: z.string().min(1),
  retentionItem: z.string().min(1),
})
export type WriteSyncPayloadPayAppNetSuite = z.infer<typeof writeSyncPayloadPayAppNetSuiteSchema>

// Some people keep line items in Quickbooks Online and others don't
// For those who don't keep line items in Quickbooks Online, we write invoices
// with a single lump sum. We also need to have the customer choose the "item" for the invoice,
// which will be displayed as the product/service for the invoice in Quickbooks Online.
export const writeSyncPayloadPayAppQuickbooksOnlineSchema = z.object({
  type: z.literal('payAppQuickbooksOnline'),
  payAppId: zodUuidSchema,
  invoiceDate: zodDaySchema,
  dueDate: zodDaySchema.optional(),
  invoiceCode: z
    .string()
    .min(1)
    .max(MAX_QUICKBOOKS_ONLINE_INVOICE_NUMBER_LENGTH)
    .optional()
    .nullable(),
  amount: z.number(),
  quickbooksItemAgaveId: z.string(),
})
export type WriteSyncPayloadPayAppQuickbooksOnline = z.infer<
  typeof writeSyncPayloadPayAppQuickbooksOnlineSchema
>

export type WriteSyncPayloadPayApp =
  | WriteSyncPayloadPayAppTextura
  | WriteSyncPayloadPayAppGcPay
  | WriteSyncPayloadPayAppProcore
  | WriteSyncPayloadPayAppSage100
  | WriteSyncPayloadPayAppFoundation
  | WriteSyncPayloadPayAppQuickbooks
  | WriteSyncPayloadPayAppFoundationFileGenie
  | WriteSyncPayloadPayAppFoundationFileFsi
  | WriteSyncPayloadPayAppComputerEase
  | WriteSyncPayloadPayAppManual
  | WriteSyncPayloadPayAppCmic
  | WriteSyncPayloadPayAppNetSuite
  | WriteSyncPayloadPayAppQuickbooksOnline

export const writeSyncPayloadPayAppLineItemsSage300Schema = z.object({
  type: z.literal('payAppLineItemsSage300'),
  payAppId: zodUuidSchema,
  payAppLineItems: z
    .array(
      z.object({
        integrationLineItemId: z.string(),
        progressBilled: z.number(),
        retention: z.number(),
        sitelineTaxGroupId: z.string().optional().nullable(),
      })
    )
    .min(0), // T&M pay apps don't require allocation
  invoiceDate: zodDaySchema,
  invoiceCode: z.string().min(1),
  dueDate: zodDaySchema,
  draw: z.string().min(1).max(MAX_SAGE_300_DRAW_NUMBER_LENGTH),
})
export type WriteSyncPayloadPayAppLineItemsSage300 = z.infer<
  typeof writeSyncPayloadPayAppLineItemsSage300Schema
>

export const writeSyncPayloadPayAppLineItemsSpectrumSchema = z.object({
  type: z.literal('payAppLineItemsSpectrum'),
  payAppId: zodUuidSchema,
  payAppLineItems: z
    .array(
      z.object({
        integrationLineItemId: z.string(),
        progressBilled: z.number(),
        retention: z.number(),
        sitelineTaxGroupId: z.string().optional().nullable(),
      })
    )
    .min(0), // T&M pay apps don't require allocation
  invoiceDate: zodDaySchema,
  invoiceCode: z.string().min(1).max(MAX_SPECTRUM_INVOICE_CODE_LENGTH),
  batchNumber: z.string().min(1).max(MAX_SPECTRUM_BATCH_NUMBER_LENGTH),
})
export type WriteSyncPayloadPayAppLineItemsSpectrum = z.infer<
  typeof writeSyncPayloadPayAppLineItemsSpectrumSchema
>

export const writeSyncPayloadPayAppLineItemsVistaSchema = z.object({
  type: z.literal('payAppLineItemsVista'),
  payAppId: zodUuidSchema,
  payAppLineItems: z
    .array(
      z.object({
        integrationLineItemId: z.string(),
        progressBilled: z.number(),
        storedMaterialsBilled: z.number().optional().nullable(),
        // For JB billing, this is retention for progress billed only
        // For AR billing, this is total retention
        retention: z.number(),
        storedMaterialsRetention: z.number().optional().nullable(),
        sitelineTaxGroupId: z.string().optional().nullable(),
      })
    )
    .min(0), // T&M pay apps don't require allocation
  invoiceDate: zodDaySchema,
  dueDate: zodDaySchema.optional(),
  invoiceCode: z.string().min(1).max(MAX_VISTA_INVOICE_NUMBER_LENGTH).optional(),
  batchNumber: z.string().min(1).max(MAX_VISTA_BATCH_NUMBER_LENGTH).optional(),
})
export type WriteSyncPayloadPayAppLineItemsVista = z.infer<
  typeof writeSyncPayloadPayAppLineItemsVistaSchema
>

export const writeSyncPayloadPayAppLineItemsAcumaticaSchema = z.object({
  type: z.literal('payAppLineItemsAcumatica'),
  payAppId: zodUuidSchema,
  payAppLineItems: z
    .array(
      z.object({
        integrationLineItemId: z.string(),
        progressBilled: z.number(),
        retention: z.number(),
        sitelineTaxGroupId: z.string().optional().nullable(),
      })
    )
    .min(0), // T&M pay apps don't require allocation
  invoiceDate: zodDaySchema,
})
export type WriteSyncPayloadPayAppLineItemsAcumatica = z.infer<
  typeof writeSyncPayloadPayAppLineItemsAcumaticaSchema
>

export const writeSyncPayloadPayAppLineItemsSageIntacctSchema = z.object({
  type: z.literal('payAppLineItemsSageIntacct'),
  payAppId: zodUuidSchema,
  payAppLineItems: z
    .array(
      z.object({
        integrationLineItemId: z.string(),
        progressBilled: z.number(),
        retention: z.number(),
        retentionReleased: z.number(),
        sitelineTaxGroupId: z.string().optional().nullable(),
      })
    )
    .min(0), // T&M pay apps don't require allocation
  invoiceDate: zodDaySchema,
  invoiceCode: z.string().min(1).optional(),
  retentionInvoiceCode: z.string().min(1).optional(),
  dueDate: zodDaySchema,
  referenceNumber: z.string().min(1).max(MAX_SAGE_INTACCT_REFERENCE_NUMBER_LENGTH).optional(),
})
export type WriteSyncPayloadPayAppLineItemsSageIntacct = z.infer<
  typeof writeSyncPayloadPayAppLineItemsSageIntacctSchema
>

// This payload supports writing a line item invoice to Quickbooks Online.
// The customer does not need to choose the item (product/service) for the invoice,
// because it will already be set in Quickbooks Online on the line item to be billed.
export const writeSyncPayloadPayAppLineItemsQuickbooksOnlineSchema = z.object({
  type: z.literal('payAppLineItemsQuickbooksOnline'),
  payAppId: zodUuidSchema,
  payAppLineItems: z.array(
    z.object({
      integrationLineItemId: z.string(),
      progressBilled: z.number(),
      retention: z.number(),
    })
  ),
  invoiceDate: zodDaySchema,
  dueDate: zodDaySchema.optional(),
  invoiceCode: z
    .string()
    .min(1)
    .max(MAX_QUICKBOOKS_ONLINE_INVOICE_NUMBER_LENGTH)
    .optional()
    .nullable(),
})
export type WriteSyncPayloadPayAppLineItemsQuickbooksOnline = z.infer<
  typeof writeSyncPayloadPayAppLineItemsQuickbooksOnlineSchema
>

export type WriteSyncPayloadPayAppLineItems =
  | WriteSyncPayloadPayAppLineItemsSage300
  | WriteSyncPayloadPayAppLineItemsSpectrum
  | WriteSyncPayloadPayAppLineItemsVista
  | WriteSyncPayloadPayAppLineItemsAcumatica
  | WriteSyncPayloadPayAppLineItemsSageIntacct
  | WriteSyncPayloadPayAppLineItemsQuickbooksOnline

export const writeSyncPayloadChangeOrderRequestVistaSchema = z
  .object({
    type: z.literal('changeOrderRequestVista'),
    changeOrderRequestIds: z.array(zodUuidSchema).min(1),
    primaryChangeOrderRequestId: zodUuidSchema,

    // We sync Change Order Requests (COR) as a single Change Order (CO) in Vista,
    // with each SOV line item being a line item on the CO.
    changeOrder: z.object({
      number: z.string().min(1),
      approvalDate: zodDaySchema,
      description: z.string().min(1).max(VISTA_CHANGE_ORDER_DESCRIPTION_MAX_CHARS).optional(),
      revisedCompletionDate: zodDaySchema.optional(),
      scheduleImpactDays: z.number().min(0).optional(),
    }),
  })
  .refine((data) => data.changeOrderRequestIds.includes(data.primaryChangeOrderRequestId), {
    message: 'Primary ID must be included in changeOrderRequestIds',
    path: ['primaryChangeOrderRequestId'],
  })
type WriteSyncPayloadChangeOrderRequestVista = z.infer<
  typeof writeSyncPayloadChangeOrderRequestVistaSchema
>

export type WriteSyncPayloadChangeOrderRequest = WriteSyncPayloadChangeOrderRequestVista

export const writeSyncPayloadLegalRequirementSchema = z.object({
  type: z.literal('legalRequirement'),
  legalRequirementId: zodUuidSchema,
  legalDocumentIds: z.array(zodUuidSchema),
})
export type WriteSyncPayloadLegalRequirement = z.infer<
  typeof writeSyncPayloadLegalRequirementSchema
>

export const writeSyncPayloadLienWaiversSchema = z.object({
  type: z.literal('lienWaivers'),
  lienWaiverIds: z.array(zodUuidSchema),
})
export type WriteSyncPayloadLienWaivers = z.infer<typeof writeSyncPayloadLienWaiversSchema>

export type WriteSyncPayload =
  | WriteSyncPayloadPayApp
  | WriteSyncPayloadPayAppLineItems
  | WriteSyncPayloadLegalRequirement
  | WriteSyncPayloadLienWaivers
  | WriteSyncPayloadChangeOrderRequest

export type WriteSyncPayloadFile =
  | WriteSyncPayloadPayAppQuickbooks
  | WriteSyncPayloadPayAppFoundationFileGenie
  | WriteSyncPayloadPayAppComputerEase
  | WriteSyncPayloadPayAppFoundationFileFsi
  | WriteSyncPayloadPayAppNetSuite

export type IntegrationProjectsFilterInput = {
  field: IntegrationProjectsFilterField
  values: string[] | null
}

export type WriteSyncResultSuccess = {
  type: 'success'
  message?: string
  warning?: string
  url?: string
  texturaDrawId?: string
  texturaDrawNumber?: number
  invoiceNumber?: string
  changeOrderNumber?: string
}

type WriteSyncResolutionUpdateLineItemRetention = {
  type: 'updateLineItemRetention'
  lineItems: {
    sitelineLineItemId: string
    description: string
    currentHeldToDate: number
    targetHeldToDate: number
  }[]
}

export type WriteSyncResolution = WriteSyncResolutionUpdateLineItemRetention

export type WriteSyncResultFailure = {
  type: 'failure'
  error: string
  code: IntegrationSyncResultCode
  resolution?: WriteSyncResolution
}

export type WriteSyncResultQueuedInHh2 = {
  type: 'queuedInHh2'
  operationId: string
  entityId: string
  entityDescription: string
}

export type WriteSyncResult =
  | WriteSyncResultSuccess
  | WriteSyncResultFailure
  | WriteSyncResultQueuedInHh2

export type FoundationIntegrationMetadata = {
  customerNumber?: string
}

export type ComputerEaseContractMetadata = {
  customerId?: string
  jobId?: string
  arAccount?: string
  salesAccount?: string
}

export type QuickbooksIntegrationMetadata = {
  projectName: string
  customerName: string
  includeRetention: boolean
  combineAsSingleLine: boolean
  singleLineDescription?: string

  /** Overrides for the default global company integration settings */
  accountsReceivableAccount?: string
  retentionAccountsReceivableAccount?: string
  progressItemName?: string
  progressItemIncomeAccount?: string
  retentionItemName?: string
  retentionItemIncomeAccount?: string
}

export type TexturaIntegrationMetadata = {
  lastProcessedMessageId?: number
}

export type NetSuiteIntegrationMetadata = {
  // The following items will override the settings on the
  // company level
  subsidiary?: string
  arAccount?: string
  progressItem?: string
  retentionItem?: string
}

export type IntegrationMetadata =
  | ComputerEaseContractMetadata
  | FoundationIntegrationMetadata
  | QuickbooksIntegrationMetadata
  | TexturaIntegrationMetadata
  | EmptyObject

export type TaxGroupIntegrationMappingsIntegration = {
  companyIntegrationId: string
  integrationTaxGroupId: string // Also called "tax code" in Agave
  name: string
}

export type TaxGroupIntegrationMappings = {
  integrations: TaxGroupIntegrationMappingsIntegration[]
}

export const getIntegrationProjectsPayloadSchema = z.object({
  filters: z
    .array(
      z.object({
        field: z.enum(IntegrationProjectsFilterField),
        values: z.array(z.string()).nullable(),
      })
    )
    .min(0)
    .optional(),
})
export type GetIntegrationProjectsPayload = z.infer<typeof getIntegrationProjectsPayloadSchema>

// Sage 100
export const bulkWriteSyncPayloadPayAppSage100Schema = z.object({
  payAppId: zodUuidSchema,
  integrationId: zodUuidSchema,
  type: z.literal('bulkSyncSage100'),
  invoiceNumber: z.string().min(1).max(MAX_SAGE_INVOICE_CODE_LENGTH),
})
export type BulkWriteSyncPayloadPayAppSage100 = z.infer<
  typeof bulkWriteSyncPayloadPayAppSage100Schema
>

// Foundation
export const bulkWriteSyncPayloadPayAppFoundationSchema = z.object({
  payAppId: zodUuidSchema,
  integrationId: zodUuidSchema,
  type: z.literal('bulkSyncFoundation'),
  invoiceNumber: z.string().min(1).max(MAX_FOUNDATION_INVOICE_NUMBER_LENGTH).optional(),
})
export type BulkWriteSyncPayloadPayAppFoundation = z.infer<
  typeof bulkWriteSyncPayloadPayAppFoundationSchema
>

// CMiC
export const bulkWriteSyncPayloadPayAppCmicSchema = z.object({
  payAppId: zodUuidSchema,
  integrationId: zodUuidSchema,
  type: z.literal('bulkSyncCmic'),
  invoiceNumber: z.string().min(1).max(MAX_CMIC_INVOICE_NUMBER_LENGTH),
})
export type BulkWriteSyncPayloadPayAppCmic = z.infer<typeof bulkWriteSyncPayloadPayAppCmicSchema>

export type BulkWriteSyncPayload =
  | BulkWriteSyncPayloadPayAppSage100
  | BulkWriteSyncPayloadPayAppFoundation
  | BulkWriteSyncPayloadPayAppCmic

export type IntegrationContractSummarySchema = {
  integrationId: string

  // These fields may be undefined for some integrations
  integrationContractTotal?: number
  integrationNetAmountBilled?: number
  integrationGrossAmountBilled?: number
  integrationAmountPaid?: number
  integrationTotalRetentionHeld?: number
}

export type ContractMismatchReportSchema = {
  contractId: string
  sitelineContractTotal: number
  sitelineNetAmountBilled: number
  sitelineGrossAmountBilled: number
  sitelineTotalRetentionHeld: number
  sitelineAmountPaid: number
  includesDrafts?: boolean
  integrationContractSummaries: IntegrationContractSummarySchema[]
}

export const vendorContractCommitmentMappingsVistaSchema = z.object({
  project: z.object({
    agaveProjectId: z.string(),
  }),
  vendor: z.object({
    agaveVendorId: z.string(),
    vendorName: z.string().optional(),
  }),
  vendorContractCommitment: z.object({
    agaveVendorContractCommitmentId: z.string(),
  }),
})

type VendorContractCommitmentMappingsVista = z.infer<
  typeof vendorContractCommitmentMappingsVistaSchema
>

export type VendorContractCommitmentMappings = VendorContractCommitmentMappingsVista

// Mappings stored on a `LienWaiverLinkedApInvoice` so the linked invoice can be traced back to
// its source AP invoice and (when applicable) the commitment/subcontract it was billed against.
// `companyIntegrationId` identifies the ERP the invoice came from, which is all we need to
// disambiguate invoices on multi-integration contracts.
const lienWaiverLinkedApInvoiceIntegrationMappingsSchema = z.object({
  companyIntegrationId: zodUuidSchema,
  integrationInvoiceId: z.string(),
  integrationContractCommitmentId: z.string().nullable(),
})
export type LienWaiverLinkedApInvoiceIntegrationMappings = z.infer<
  typeof lienWaiverLinkedApInvoiceIntegrationMappingsSchema
>

// One mapping between a set of Siteline line items and a set of Integration line items
const sovLineItemIntegrationMappingSchema = z.object({
  sitelineLineItemIds: z.array(zodUuidSchema).min(1),
  integrationLineItemIds: z.array(zodIntegrationEntityIdSchema).min(1),
})
export type SovLineItemIntegrationMapping = z.infer<typeof sovLineItemIntegrationMappingSchema>

// A set of line item mappings for a single integration
export const sovIntegrationMappingSchema = z.object({
  integrationId: zodUuidSchema,
  lineItems: z.array(sovLineItemIntegrationMappingSchema),
})

// Used for `Sov.integrationMappings`
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sovIntegrationMappingsSchema = z.object({
  mappings: z.array(sovIntegrationMappingSchema),
})
export type SovIntegrationMappings = z.infer<typeof sovIntegrationMappingsSchema>
